/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { useEffect, useState } from "react";

import Topheader from "../../Layout/header/header";
import styles from "./featureList.module.css";
import CloseIcon from "@mui/icons-material/Close";
import {
  TextField,
  Button,
  Box,
  InputAdornment,
  Modal,
  Select,
  MenuItem,
  Snackbar,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Sidebar } from "../../Components/Sidebar";

import PopupDialog from "../../Components/PopupDialog";

import FormControl from "@mui/material/FormControl";
import APIManager from "../../Services/ApiManager/ApiManager";
import noappsimage from "../../assets/images/noapps.png";
import { useNavigate } from "react-router-dom";
import { FeatureTable } from "../../Components/FeatureTable";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "4px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};
export default function FeatureList() {

  const FEATURE_NAME_MAX_LENGTH = 50;
  const DEFAULT_FEATURE_NAME = "Adaptive Suspension";
  const DEFAULT_FEATURE_UNIQUE_ID = "com.harman.features.feature-ID";

  const [featureList, setFeatureList] = useState([]);
  const [searchFeatureList, setSearchFeatureList] = useState([]);
  const [searchString, setSearchString] = useState("");
  const accessToken = "Bearer " + sessionStorage.getItem("access_token");
  const [loader, setLoader] = useState(false);
  const [openFeatureCreateModal, setOpenFeatureCreateModal] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [componentName, setComponentName] = useState("");
  const [featureName, setFeatureName] = useState("");
  const [selectedLang, setSelectedLang] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [featureUniqueId, setFeatureUniqueId] = useState("");
  const [featureId, setFeatureId] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState([]);
  const [associateTeam, setAssociateTeam] = useState([]);
  const [showNoFeatureImage, setShowNoFeatureImage] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [isCreateEnabled, setIsCreateEnabled] = useState(false);

  const navigate = useNavigate();
  const myHeaders = new Headers();

  myHeaders.append("authorization", accessToken);

  const handleFeatureName = (event) => {
    setFeatureName(event.target.value);
    if (event.target.value === "") {
      return;
    }
  };

  const handleAssociateTeamChange = (event) => {
    setSelectedTeam(event.target.value);
  };

  const handleFeatureUniqueId = (event) => {
  const value = event.target.value;
  setFeatureUniqueId(value);

  if (
    value === "" ||
    !isValidFeatureUniqueId(value)
  ) {
    return;
  }
};

  const getFeatureBasedOnStatus = (props) => {
    setLoader(true);
    if (props === "all") {
      getAllFeatures();
    } else {
      getAllFeaturesBasedOnStatus(props);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setOpenFeatureCreateModal(false);
    setLoader(true);
    const formData = new FormData();
  
    try {
      const response = APIManager.postCreateFeature(featureName, selectedLang, selectedTeam, featureUniqueId, formData);
      if (response.ok) {
        const data = await response.json();

        setFeatureId(data.applicationId);

        setFeatureName("");
        setSelectedLang("");
        setSelectedTeam("");

        handleClose();
        setLoader(false);
        navigate(`/ui/developer/applications/${data.applicationId}`, {
          state: { teamId: selectedTeam },
        });
      } else {
        const data = await response.json();

        handleClose();
        setOpenErrorPopup(true);
        setHeaderMessage("Error");
        setComponentName("appError");
        setErrorMessage(data[0].message);
        setLoader(false);
        setOpenSnackbar(false);
      }
    } catch (error) {
      console.error("Error creating feature:", error);
      handleClose();
      setOpenErrorPopup(true);
      setHeaderMessage("Error");
      setComponentName("appError");
      setErrorMessage("Failed to create new feature, please create again!");
      setLoader(false);
      setOpenSnackbar(false);
    }
  };

  function handleSearchFilter(e) {
    const value = e.target.value;
    setSearchString(value);
  
    if (value.length === 0) {
      setSearchFeatureList([]); /* Reset the filtered list if input is empty */
      setTableLoader(false);
      return;
    }
  
    if (value.length > 1) {
      const res = featureList.filter(
        (obj) =>
          obj.applicationName.toLowerCase().includes(value.toLowerCase()) ||
          obj.packageName.toLowerCase().includes(value.toLowerCase())
      );
      setSearchFeatureList(res);
      setTableLoader(false);
    }
  }

  function getAllLanguages() {
    setLoader(true);
    APIManager.getLanguages("en-US").then((json) => {
      if (json[0].message === undefined) {
        const defaultSetLang = json.find(
          (lang) => lang.languageTag === "en-US"
        );

        setSelectedLang(defaultSetLang.languageTag);
        setDefaultLanguage(json);

        setLoader(false);
      } else {
        setComponentName("appError");
        setErrorMessage(json[0].message);
        setOpenErrorPopup(true);
        setLoader(false);
      }
    });
  }

  function getTeamsLanguages() {
    setLoader(true);
    APIManager.getAllTeams().then((json) => {
      if (json[0].message === undefined) {
        /* Find team with both "default" and "team" in the name (case-insensitive) */
        const defaultTeam = json.find(team => {
          const name = team.name.toLowerCase();
          return name.includes("default") && name.includes("team");
        });
        if (defaultTeam) {
          setSelectedTeam(defaultTeam.id);
        } else if (json.length === 1) {
          setSelectedTeam(json[0].id);
        }
        setAssociateTeam(json);
        setLoader(false);
      } else {
        setComponentName("appError");
        setErrorMessage(json[0].message);
        setOpenErrorPopup(true);
        setLoader(false);
      }
    });
  }

  function handleAddNewFeatureModalOpen() {
    setFeatureName("");
    setSelectedLang("");
    setSelectedTeam("");
    getAllLanguages();
    getTeamsLanguages();
    setFeatureUniqueId("");
    setOpenFeatureCreateModal(true);
  }

  const handleClose = () => {
    setFeatureName("");
    setSelectedLang("");
    setSelectedTeam("");
    setFeatureUniqueId("");
    setLoader(false);
    setOpenFeatureCreateModal(false);
  };

  const isValidFeatureUniqueId = (value) => {
    if (/\s/.test(value)) return false; /* No spaces allowed */
    const parts = value.split(".");
    if (!(parts.length === 3 || parts.length === 4)) return false;
    if (parts.some(p => p.length === 0)) return false; /* No empty parts */

    /* Check that 2nd, 3rd, 4th parts (if exist) do not start with '-' */
    for (let i = 1; i < parts.length; i++) {
      if (parts[i].startsWith("-")) return false;
    }
    return true;
  };

  function canEnableCreateButton() {
  return (
    featureName.trim().length > 0 &&
    selectedTeam.trim().length > 0 &&
    selectedLang.trim().length > 0 &&
    featureUniqueId.trim().length > 0 &&
    isValidFeatureUniqueId(featureUniqueId)
  );
}
  const handleDeleteFeature = async (featureData) => {
    setLoader(true);

    try {
      const response = APIManager.deleteFeature(featureData);

      if (response.status === 204) {
        getAllFeatures();
        window.scrollTo(0, 0);
        setOpenSnackbar(true);
        setSnackMsg(
          `Feature ${featureData.applicationName} is deleted successfully`
        );
      } else {
        const data = await response.json();

        if (data !== undefined && data.length > 0) {
          setOpenErrorPopup(true);
          setHeaderMessage("Delete Error");
          setComponentName("deleteError");
          setErrorMessage(data[0].message);
          setLoader(false);
          setOpenSnackbar(false);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setLoader(false);
    }
  };

  function getAllFeatures() {
    setLoader(true);
    APIManager.getAllFeatures()
      .then((json) => {
        setFeatureList([]);

        if (json.length > 0) {
          if (json[0].message === undefined) {
            setFeatureList(json);
            setLoader(false);
          } else {
            setErrorMessage(json[0].message);
            setOpenErrorPopup(true);
            setLoader(false);
          }
        } else {
          setShowNoFeatureImage(true);
          setLoader(false);
        }
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        if (error.message === "jwt expired") {
          setHeaderMessage("401 Token Expired");
        } else {
          setHeaderMessage("Error");
        }
        setOpenErrorPopup(true);
        setLoader(false);
      });
  }

  function getAllFeaturesBasedOnStatus(status) {
    setLoader(true);
    APIManager.getAllFeaturesBasedOnStatus(status)
      .then((json) => {
        setFeatureList([]);

        if (json.length > 0) {
          if (json[0].message === undefined) {
            setFeatureList(json);
            setLoader(false);
          } else {
            setErrorMessage(json[0].message);
            setOpenErrorPopup(true);
            setLoader(false);
          }
        } else {
          setFeatureList([]);
          setShowNoFeatureImage(true);
          setLoader(false);
        }
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        if (error.message === "jwt expired") {
          setHeaderMessage("401 Token Expired");
        } else {
          setHeaderMessage("Error");
        }
        setOpenErrorPopup(true);
        setLoader(false);
      });
  }

  const handlePopUpClose = () => {
    setOpenErrorPopup(false);
  };

  useEffect(() => {
    setLoader(true);
    APIManager.getCognitoSelf()
      .then((json) => {
        if (
          json[0] !== undefined &&
          json[0].detailedErrorCode === "USER_AUTHENTICATION_EXCEPTION"
        ) {
          setErrorMessage(json[0].message);
          setOpenErrorPopup(true);
          setHeaderMessage("401 Unauthorized");
          setComponentName("userAuthFail");
          setLoader(false);
        }
        if (
          json.results !== undefined &&
          json?.messages[0]["key"] !== "success.key"
        ) {
          navigate(`/developer/myprofile`);
        }

        if (
          json.results !== undefined &&
          json?.messages[0]["key"] === "success.key"
        ) {
          sessionStorage.setItem("userId", json.results[0]["id"]);
          getAllFeatures();
        }
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        if (error.message === "jwt expired") {
          setHeaderMessage("401 Token Expired");
        } else {
          setHeaderMessage("Error");
        }
        setOpenErrorPopup(true);
        setLoader(false);
      });
  }, [featureId]);

  useEffect(() => {
    setIsCreateEnabled(canEnableCreateButton());
  }, [featureName, selectedLang, selectedTeam, featureUniqueId]);

  return (
    <div className={styles.featureListContainer}>
      <div>
        <Topheader />
      </div>

      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {showNoFeatureImage && (
        <div className={styles.noFeaturesDivCls}>
          <img
            className={styles.noFeaturesImagecls}
            src={noappsimage}
            alt="no feature img"
          />
          <div className={styles.noFeaturesWelcomeTextcls}>
            Welcome to the Ignite Store Developer Console!
          </div>
          <div className={styles.noFeaturesTextcls}>
            All your features will be listed here.
          </div>
          <div className={styles.noFeaturesTextcls}>
            Start by creating your first feature listing.
          </div>
          <Button
            style={{
              background: "221.2 83.2% 53.3%",
              textTransform: "none",
              height: "36px",
              marginTop: "12px",
            }}
            variant="contained"
            onClick={handleAddNewFeatureModalOpen}
          >
            Add New Feature
          </Button>
        </div>
      )}

      {featureList?.length > 0 && (
        <div className={styles.tableContainer}>
          {/* Sidebar (left column) */}
          <div className={styles.sideNavContainer}>
            <Sidebar
              itemList={featureList}
              getItemBasedOnStatus={getFeatureBasedOnStatus}
              props={{ type: "Feature" }}
            />
          </div>
          {/* Main content (right column) */}
          <div className={styles.mainTableColumn}>
            <div className={styles.mainTableContent}>
              {/* Header row: search and button */}
              <div className={styles.tableListHeader}>
                <span className={styles.headerContentCls}>Features</span>
                <Box
                  className={styles.searchCreateBtnCls}
                  component="form"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    "& .MuiTextField-root": {
                      width: "38ch",
                    },
                  }}
                  noValidate
                  autoComplete="off"
                >
                  <TextField
                    type="search"
                    placeholder="Search for feature name"
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                    onChange={handleSearchFilter}
                  />
                  <Button
                    style={{
                      background: "221.2 83.2% 53.3%",
                      textTransform: "none",
                      height: "36px",
                      marginLeft: "8px",
                    }}
                    variant="contained"
                    onClick={handleAddNewFeatureModalOpen}
                  >
                    Add New Feature
                  </Button>
                </Box>
              </div>
              {/* Table row */}
              <div className={styles.featureTableWrapper}>
                <FeatureTable
                  featureList={searchString.length === 0 ? featureList : searchFeatureList}
                  tableLoader={tableLoader}
                  deleteFeature={handleDeleteFeature}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={openFeatureCreateModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={{ ...style, width: 583 }}>
          <div className={styles.modalHeaderCls}>
            <h2 className={styles.modalHeadingCls}>New Feature Listing</h2>
            <CloseIcon onClick={handleClose} />
          </div>
          <div>&nbsp;</div>
          <div>
            <form onSubmit={handleSubmit}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <div style={{ position: "relative" }}>
                  <label htmlFor="featureName" className={styles.labelName}>
                    Feature Name<span className={styles.requiredAsterisk}>*</span>
                  </label>
                  <TextField
                    onChange={handleFeatureName}
                    fullWidth
                    value={featureName}
                    id="featureName"
                    placeholder={DEFAULT_FEATURE_NAME}
                    slotProps={{
                      input: { maxLength: FEATURE_NAME_MAX_LENGTH }
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: 8,
                      bottom: -22,
                      fontSize: "12px",
                      color: "#888",
                    }}
                  >
                    {`${FEATURE_NAME_MAX_LENGTH - featureName.length}/${FEATURE_NAME_MAX_LENGTH}`}
                  </span>
                </div>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <label htmlFor="featureUniqueID" className={styles.labelName}>
                  Feature Unique ID<span className={styles.requiredAsterisk}>*</span>
                </label>
                <TextField
                  onChange={handleFeatureUniqueId}
                  fullWidth
                  value={featureUniqueId}
                  id="featureUniqueID"
                  placeholder={DEFAULT_FEATURE_UNIQUE_ID}
                  helperText="Unique identifier for the feature"
                />
              </FormControl>
              <div className={styles.selectFormControl}>
                <FormControl className={styles.halfWidthColumn}>
                  <label htmlFor="defaultLanguageSelect" className={styles.labelName}>
                    Default Language<span className={styles.requiredAsterisk}>*</span>
                  </label>
                  <Select
                    id="defaultLanguageSelect"
                    value={selectedLang}
                  >
                    {defaultLanguage.map((language) => (
                      <MenuItem
                        key={language.languageTag}
                        value={language.languageTag}
                      >
                        {language.defaultName} ({language.languageTag})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl className={styles.halfWidthColumn}>
                  <label htmlFor="associateTeamSelect" className={styles.labelName}>
                    Associate Team<span className={styles.requiredAsterisk}>*</span>
                  </label>
                  <Select
                    id="associateTeamSelect"
                    onChange={handleAssociateTeamChange}
                  >
                    {associateTeam.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div>&nbsp;</div>
              <div className={styles.modalFooterCls}>
                <Button
                  style={{
                    background: "#1214151A",
                    textTransform: "none",
                    height: "36px",
                    width: "116px",
                    color: "#2A2C2D",
                    fontWeight: "500",
                    fontSize: "14px",
                    lineHeight: "16.41px",
                  }}
                  variant="contained"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                &nbsp;&nbsp;
                <Button
                  style={{
                    background: "221.2 83.2% 53.3%",
                    textTransform: "none",
                    height: "36px",
                    width: "116px",
                    fontWeight: "500",
                    fontSize: "14px",
                    lineHeight: "16.41px",
                  }}
                  variant="contained"
                  type="submit"
                  disabled={!isCreateEnabled}
                >
                  Create
                </Button>
              </div>
            </form>
          </div>
        </Box>
      </Modal>
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={handlePopUpClose}
        type={"feature"}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
      <div>&nbsp;</div>
    </div>
  );
}
